import {
  cloneElement,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { Tooltip as BaseUITooltip } from '@base-ui/react/tooltip';

function joinClasses(...values) {
  return values.filter(Boolean).join(' ');
}

function resolveSide(position) {
  switch (position) {
    case 'left':
    case 'right':
    case 'bottom':
    case 'top':
      return position;
    default:
      return 'top';
  }
}

function resolveSideOffset(side) {
  return side === 'left' || side === 'right' ? 8 : 6;
}

const activeTooltipListeners = new Set();
let openTooltipCount = 0;

function closeManagedTooltips(activeTooltipId = null, event = null) {
  activeTooltipListeners.forEach((listener) => {
    listener(activeTooltipId, event);
  });
}

function handleGlobalDismiss(event) {
  if (openTooltipCount === 0) {
    return;
  }

  if (event?.type === 'keydown' && event.key !== 'Escape') {
    return;
  }

  if (event?.type === 'visibilitychange' && document.visibilityState === 'visible') {
    return;
  }

  closeManagedTooltips(null, event);
}

function addGlobalDismissListeners() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.addEventListener('pointerdown', handleGlobalDismiss, true);
  window.addEventListener('pointercancel', handleGlobalDismiss, true);
  window.addEventListener('mousedown', handleGlobalDismiss, true);
  window.addEventListener('touchstart', handleGlobalDismiss, true);
  window.addEventListener('wheel', handleGlobalDismiss, { capture: true, passive: true });
  window.addEventListener('resize', handleGlobalDismiss, true);
  window.addEventListener('blur', handleGlobalDismiss, true);
  window.addEventListener('dragstart', handleGlobalDismiss, true);
  window.addEventListener('keydown', handleGlobalDismiss, true);
  document.addEventListener('scroll', handleGlobalDismiss, true);
  document.addEventListener('visibilitychange', handleGlobalDismiss, true);
}

function removeGlobalDismissListeners() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.removeEventListener('pointerdown', handleGlobalDismiss, true);
  window.removeEventListener('pointercancel', handleGlobalDismiss, true);
  window.removeEventListener('mousedown', handleGlobalDismiss, true);
  window.removeEventListener('touchstart', handleGlobalDismiss, true);
  window.removeEventListener('wheel', handleGlobalDismiss, true);
  window.removeEventListener('resize', handleGlobalDismiss, true);
  window.removeEventListener('blur', handleGlobalDismiss, true);
  window.removeEventListener('dragstart', handleGlobalDismiss, true);
  window.removeEventListener('keydown', handleGlobalDismiss, true);
  document.removeEventListener('scroll', handleGlobalDismiss, true);
  document.removeEventListener('visibilitychange', handleGlobalDismiss, true);
}

function subscribeToManagedTooltips(listener) {
  activeTooltipListeners.add(listener);

  if (activeTooltipListeners.size === 1) {
    addGlobalDismissListeners();
  }

  return () => {
    activeTooltipListeners.delete(listener);

    if (activeTooltipListeners.size === 0) {
      removeGlobalDismissListeners();
    }
  };
}

function BaseTooltip({
  content,
  children,
  position = 'auto',
  delay = 500,
  defaultOpen = false,
  interactive = false,
  triggerClassName = '',
  popupClassName = '',
  portalRoot = null,
  disabled = false,
}) {
  const tooltipId = useId();
  const [open, setOpen] = useState(defaultOpen && !disabled);
  const popupRef = useRef(null);

  useEffect(() => {
    return subscribeToManagedTooltips((activeTooltipId, event) => {
      if (activeTooltipId === tooltipId) return;
      // A press, scroll or drag inside this tooltip's own popup is using it, not leaving it:
      // closing here unmounted the influence slider under the pointer mid-drag. Other tooltips
      // still close, and Escape always closes. Non-interactive popups take no pointer events,
      // so they can never be the target. Resize and window blur target the window, which is
      // not a node, and `contains` throws on it.
      if (event?.type !== 'keydown' && event?.target instanceof globalThis.Node
        && popupRef.current?.contains(event.target)) return;
      setOpen(false);
    });
  }, [tooltipId]);

  useEffect(() => {
    if (disabled) {
      closeManagedTooltips();
    }
  }, [disabled]);

  useEffect(() => {
    if (open && !disabled) {
      closeManagedTooltips(tooltipId);
    }
  }, [disabled, open, tooltipId]);

  useEffect(() => {
    if (!(open && !disabled)) return undefined;
    openTooltipCount += 1;
    return () => {
      openTooltipCount -= 1;
    };
  }, [disabled, open]);

  const handleOpenChange = useCallback((nextOpen) => {
    const resolvedOpen = nextOpen && !disabled;
    setOpen(resolvedOpen);

    if (resolvedOpen) {
      closeManagedTooltips(tooltipId);
    }
  }, [disabled, tooltipId]);

  if (content == null || content === false || content === '') {
    return children;
  }

  const side = resolveSide(position);
  const sideOffset = resolveSideOffset(side);
  const isDirectDomTrigger = isValidElement(children)
    && children.type !== Fragment
    && typeof children.type === 'string';

  const triggerElement = isDirectDomTrigger
    ? cloneElement(children, {
        className: joinClasses(children.props.className, triggerClassName),
      })
    : (
        <span className={joinClasses('base-tooltip__trigger', triggerClassName)}>
          {children}
        </span>
      );

  return (
    <BaseUITooltip.Root
      open={open && !disabled}
      onOpenChange={handleOpenChange}
      disableHoverablePopup={!interactive}
      disabled={disabled}
    >
      <BaseUITooltip.Trigger
        delay={delay}
        closeDelay={interactive ? 100 : 0}
        disabled={disabled}
        render={triggerElement}
      />
      <BaseUITooltip.Portal container={portalRoot || undefined}>
        <BaseUITooltip.Positioner
          side={side}
          align="center"
          sideOffset={sideOffset}
          positionMethod="fixed"
          className="base-tooltip__positioner"
        >
          <BaseUITooltip.Popup
            ref={popupRef}
            className={joinClasses(
              'base-tooltip__popup',
              interactive && 'base-tooltip__popup--interactive',
              popupClassName,
            )}
          >
            {content}
            <BaseUITooltip.Arrow className="base-tooltip__arrow" />
          </BaseUITooltip.Popup>
        </BaseUITooltip.Positioner>
      </BaseUITooltip.Portal>
    </BaseUITooltip.Root>
  );
}

export default BaseTooltip;
